import { Box, Icon, Typography } from '@mui/material'
import {
    memo,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    Applications,
    FMTABS,
    IN_TRANSIT_TABS,
    locationMarkerSVG,
    periodOptions,
    publicShipmentView,
    URLSearchParamsKeys,
    widgetView,
} from '../../../constants/constants'
import { fetchMapData } from '../../../services/entityServices'
import dayjs from 'dayjs'
import { useAppSelector } from '../../../redux/hook'
import utc from 'dayjs/plugin/utc'
import {
    IDateTime,
    ISelectedAttributes,
    ISelfAttributesRelationsData,
    SelectedRelationList,
    IReportSettings,
} from '../interfaces'
import { IEntityInstanceRelationsResponse } from '../../../services/apiInterfaces'
import {
    getAttributeById,
    isChartableAndNotHidden,
    isChartableOrMappableOrTimeSeriesAndNotHidden,
    isMappable,
    isMappableEntityAttribute,
    isMappableTimeSeriesAndNotHidden,
    isTimeSeries,
} from '../../../utils/AttributeUtils'
import { Fullscreen, FullscreenExit } from '@mui/icons-material'
import { IAttribute } from '../../DTStudio/interfaces'
import {
    getRandomDarkColor,
    getDataSourceKey,
    isCriticalAlarm,
} from '../../../utils/MiscellaneousUtils'
import {
    calculatePolygonCenter,
    // getLatestUniqueLocations,
    isValidGeoJSON,
    isValidMapData,
} from '../../../utils/MapUtils'
import MaritimeReport from './MaritimeReportView/MaritimeReport'
import {
    Distance,
    InTransitSettingsType,
} from '../../Applications/Intransit/interfaces'
import { flattenRelatedEntities } from '../../../utils/EntityUtils'
import GeneralReportView from './PDFReportView/GeneralReportView'
import { useLocation, useSearchParams } from 'react-router-dom'
import { IApplicationSettingsType } from '../../Applications/interfaces'
import { IFacilityMonitoringSettingsType } from '../../Applications/FacilityManagement/interfaces'
import {
    convertDateToTargetTimezone,
    getMaximumDate,
    getMinimumDate,
    isDate1BiggerThanDate2,
} from '../../../utils/DateAndTimeUtils'
import Alerts from './AlertsView/Alerts'
import Insurance from './InsuranceView/Insurance'
import TimeSeriesDataTable from '../Components/TimeSeriesDataTable'
import useDebounce from '../../../CustomHooks/useDebounce'
import ViewEntityAttributeFilterPanel from './ViewFilters/ViewEntityAttributeFilterPanel'
import ViewEntityTabs from './ViewEntityTabs'
import { useTranslation } from 'react-i18next'
import ApacheEcharts from './GraphView/Graphs/ApacheEcharts'
import AnalysisView from './AnalysisView'
import MultiDataSourceMapComponent, {
    GeoJSONDataSource,
} from './MapView/DynamicDataSourceMap'
import {
    IKeyValuePair,
    IShipmentWaypoint,
    IWayPointAndPeriod,
} from '../../Entity/interfaces'
import { getRelationNameByTargetEntityTemplateId } from '../../../utils/RelationUtils'
import CarbonReportFromWaypoints from './CarbonReportView/CarbonReportFromWaypoints'
import { useQuery } from '@tanstack/react-query'
import { formatEntityDisplayLabel } from '../../../utils/entityDisplayUtils'

dayjs.extend(utc)

interface ITabsViewProps {
    graphExpand: boolean
    setGraphExpand: (value: boolean) => void
    applicationSettings: IApplicationSettingsType
}

const TabsView = (props: ITabsViewProps): ReactElement => {
    const {
        graphExpand,
        setGraphExpand,
        applicationSettings = {} as IApplicationSettingsType,
    } = props
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()

    const isGuestUser =
        useAppSelector((state) => state.searchParams.isGuestUser) === 'true'
    const publicSearchTemplateId =
        useAppSelector((state) => state.searchParams.templateId) || ''
    const loggedInOrgId = useAppSelector((state) => state.org.org?.id)
    const publicSearchOrgId =
        useAppSelector((state) => state.searchParams.orgId) || ''

    const publicSearchViewId = useAppSelector(
        (state) => state.searchParams.instanceId,
    )

    const isPublicShipmentPreView =
        useAppSelector((state) => state.searchParams.publicShipmentView) ===
        publicShipmentView.SHIPMENT_PREVIEW
    const orgId = isPublicShipmentPreView
        ? publicSearchOrgId
        : loggedInOrgId || ''
    const entityTemplateId = isPublicShipmentPreView
        ? publicSearchTemplateId
        : searchParams.get(URLSearchParamsKeys.templateId) || ''
    const viewId =
        (isPublicShipmentPreView
            ? publicSearchViewId
            : searchParams.get(URLSearchParamsKeys.instanceId)) || ''

    const user = useAppSelector((state) => state.user.user)
    const viewTab: string = useAppSelector(
        (state) => state.entityInstanceView.viewTab,
    )
    const entityData = useAppSelector(
        (state) => state.entityInstanceView.entityData,
    )
    const entityTemplateRelations = useAppSelector(
        (state) => state.entityInstanceView.entityTemplateRelations,
    )
    const guestUserPreferredUnit = useAppSelector(
        (state) => state.entityInstanceView.guestUserPreferredUnit,
    )

    const {
        selfEntityAttributeData,
        relatedEntitiesResponse = {} as IEntityInstanceRelationsResponse,
        entityInstanceCreatedDate,
        entityInstanceEndDate,
        earliestDataTime,
        alarmStatus,
        shipmentStatus,
        enabledApplications,
    } = entityData

    const enabledApplicationsFromStore = useAppSelector(
        (state) => state.org.enabledApplications,
    )
    const enabledApplication2 = isGuestUser
        ? enabledApplications
        : enabledApplicationsFromStore

    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const tabIndex = queryParams.get('tabIndex') || ''
    const applicationId = isPublicShipmentPreView
        ? Applications.inTransit.id
        : queryParams.get('applicationId') || ''

    const isFacilityMonitoringAssetTabView =
        applicationId === Applications.facilityMonitoring.id &&
        Number.parseInt(tabIndex) === FMTABS.ASSET_VIEW

    const isIntransitEntityInstanceView =
        (applicationId === Applications.inTransit.id &&
            Number.parseInt(tabIndex) === IN_TRANSIT_TABS.MAP_VIEW) ||
        isPublicShipmentPreView

    const allRelatedEntities = useMemo(
        () => flattenRelatedEntities(relatedEntitiesResponse) || [],
        [relatedEntitiesResponse],
    )

    const {
        inTransitSettings,
        facilityMonitoringSettings,
        isIntransitShipmentPreview,
        isFMAssetPreview,
    } = useMemo(() => {
        let inTransitSettings = {} as InTransitSettingsType
        let facilityMonitoringSettings = {} as IFacilityMonitoringSettingsType
        /**
         * isIntransitShipmentPreview and isIntransitEntityInstanceView both serve different purpose.
         * Even in CCL Map View, we could display another instance preview which can be an instance of a different template.
         * This was possible when we have navigation upon clicking the related entities. But right now (Aug 12 2025) this navigation
         * is disabled in CCL Shipment preview due to customization of Shipment Preview in CCL Application.
         */
        let isIntransitShipmentPreview = false
        let isFMAssetPreview = false
        if (isIntransitEntityInstanceView) {
            isIntransitShipmentPreview =
                (applicationSettings as InTransitSettingsType)
                    .shipmentEntityTemplateId === entityTemplateId
            if (isIntransitShipmentPreview) {
                inTransitSettings = applicationSettings as InTransitSettingsType
            }
        } else if (isFacilityMonitoringAssetTabView) {
            isFMAssetPreview =
                (applicationSettings as IFacilityMonitoringSettingsType)
                    .assetEntityTemplateId === entityTemplateId
            if (isFMAssetPreview) {
                facilityMonitoringSettings =
                    applicationSettings as IFacilityMonitoringSettingsType
            }
        }

        return {
            inTransitSettings,
            facilityMonitoringSettings,
            isIntransitShipmentPreview,
            isFMAssetPreview,
        }
    }, [
        applicationSettings,
        isIntransitEntityInstanceView,
        entityTemplateId,
        isFacilityMonitoringAssetTabView,
    ])

    const searatesRelationName = getRelationNameByTargetEntityTemplateId(
        entityTemplateRelations || [],
        inTransitSettings?.searatesEntityTemplateId,
    )
    const searatesEntityInstances = useMemo(
        () => relatedEntitiesResponse?.relations?.[searatesRelationName],
        [relatedEntitiesResponse, searatesRelationName],
    )

    const shouldDisplayGeneralReportsTab =
        isIntransitShipmentPreview || isFMAssetPreview

    const reportSettings: IReportSettings = {} as IReportSettings
    if (isIntransitShipmentPreview) {
        reportSettings.reportDataFromAttributeId =
            inTransitSettings.reportDataFromAttributeId
        reportSettings.reportDataUntilAttributeId =
            inTransitSettings.reportDataUntilAttributeId
        reportSettings.reportIdAttributeId =
            inTransitSettings.reportIdAttributeId
        reportSettings.reportURLAttributeId =
            inTransitSettings.reportURLAttributeId
        reportSettings.reportedEntityTemplateId =
            inTransitSettings.shipmentEntityTemplateId
        reportSettings.reportingObjectAttributeId =
            inTransitSettings.reportingObjectAttributeId
        reportSettings.reportsEntityTemplateId =
            inTransitSettings.reportsEntityTemplateId
    } else if (isFMAssetPreview) {
        reportSettings.reportDataFromAttributeId =
            facilityMonitoringSettings.reportDataFromAttributeId
        reportSettings.reportDataUntilAttributeId =
            facilityMonitoringSettings.reportDataUntilAttributeId
        reportSettings.reportIdAttributeId =
            facilityMonitoringSettings.reportIdAttributeId
        reportSettings.reportURLAttributeId =
            facilityMonitoringSettings.reportURLAttributeId
        reportSettings.reportedEntityTemplateId =
            facilityMonitoringSettings.assetEntityTemplateId
        reportSettings.reportingObjectAttributeId =
            facilityMonitoringSettings.reportingObjectAttributeId
        reportSettings.reportsEntityTemplateId =
            facilityMonitoringSettings.reportEntityTemplateId
    }

    const filterOutlierLocations = isIntransitShipmentPreview
        ? inTransitSettings?.filterOutlierLocations === 'true'
        : false

    const dataList: ISelfAttributesRelationsData[] = useMemo(() => {
        const selfAttributesDataList: ISelfAttributesRelationsData[] =
            selfEntityAttributeData?.attributes
                ?.filter(
                    isIntransitShipmentPreview
                        ? isChartableAndNotHidden
                        : isChartableOrMappableOrTimeSeriesAndNotHidden,
                )
                ?.map((attribute) => {
                    return {
                        id: viewId + attribute.id,
                        selfAttribute: attribute,
                        isRelation: false,
                    } as ISelfAttributesRelationsData
                })
        const relatedEntitiesDataList: ISelfAttributesRelationsData[] =
            allRelatedEntities
                .filter((relatedEntityInstance) => {
                    if (isIntransitShipmentPreview) {
                        /**
                         * In Shipment Preview we need to display only the devices in the first dropdown.
                         * Below check ensures that the instances having chartable attributes are selected
                         * which means only Device instances are selected while the Maritime, RED, Insurance are omitted.
                         */
                        const hasChartableAttributes =
                            relatedEntityInstance.attributes.some(
                                isChartableAndNotHidden,
                            )
                        return hasChartableAttributes
                    } else {
                        return true
                    }
                })
                .map((relatedEntityInstance) => {
                    return {
                        id: relatedEntityInstance.entityInstanceId,
                        isRelation: true,
                        relatedEntityInstance,
                    }
                })

        return [...(selfAttributesDataList || []), ...relatedEntitiesDataList]
    }, [
        selfEntityAttributeData,
        allRelatedEntities,
        isIntransitShipmentPreview,
        viewId,
    ])

    const allMappableTimeSeriesAttributes: ISelectedAttributes[] =
        useMemo(() => {
            const allMappableTimeSeriesAttributes: ISelectedAttributes[] = []
            selfEntityAttributeData?.attributes?.forEach(
                (attribute: IAttribute) => {
                    if (isMappableTimeSeriesAndNotHidden(attribute)) {
                        allMappableTimeSeriesAttributes.push({
                            entityInstanceId: viewId,
                            attribute,
                        })
                    }
                },
            )
            /**
             * Below code block includes Location(Device), AIS Location, PIN Location mappable timeseries in non CCL shipment Previews
             */
            if (!isIntransitShipmentPreview) {
                allRelatedEntities.forEach((relatedEntity) => {
                    relatedEntity.attributes.forEach((attribute) => {
                        if (isMappableTimeSeriesAndNotHidden(attribute)) {
                            allMappableTimeSeriesAttributes.push({
                                entityInstanceId:
                                    relatedEntity.entityInstanceId,
                                attribute,
                            })
                        }
                    })
                })
            }
            return allMappableTimeSeriesAttributes
        }, [
            selfEntityAttributeData,
            allRelatedEntities,
            isIntransitShipmentPreview,
            viewId,
        ])

    const initializeSelectedRelationListForRelatedEntity = useCallback(
        (item: ISelfAttributesRelationsData): SelectedRelationList => {
            const firstAttributeForAutoSelect =
                item?.relatedEntityInstance?.attributes.find(
                    isIntransitShipmentPreview
                        ? isChartableAndNotHidden
                        : isChartableOrMappableOrTimeSeriesAndNotHidden,
                )
            const selectedAttributes = firstAttributeForAutoSelect
                ? [firstAttributeForAutoSelect]
                : []
            return {
                relatedEntityInstanceId: item?.id,
                selectedAttributes,
            } as SelectedRelationList
        },
        [isIntransitShipmentPreview],
    )

    const {
        initialSelectedValues = [],
        initialSelectedRelationList = [],
        initialCustomStartDate,
        initialCustomEndDate,
    } = useMemo(() => {
        /**
         * For intransit view the start date and end date should be based on the preselected self dynamic attributes or
         * pre selected relational attributes.
         * In other cases like facility monitoring asset view or any normal entity instance view the start date and
         * end date duration should be 1 day only so that we want to limit the graph data.
         */

        let initialCustomStartDate: string = dayjs
            .utc()
            .subtract(1, 'day')
            .format()
        let initialCustomEndDate: string = dayjs.utc().format()
        const listOfStartDates: (string | null)[] = []
        const listOfEndDates: (string | null)[] = []

        if (isIntransitShipmentPreview || isFMAssetPreview) {
            /**
             * Pre select mappable attributes, relations and attributes of relations if we are coming from intransit map view
             */
            let preSelectedChartableAttributes
            if (isIntransitShipmentPreview) {
                preSelectedChartableAttributes =
                    inTransitSettings.preSelectedChartableAttributes
            } else {
                preSelectedChartableAttributes =
                    facilityMonitoringSettings.preSelectedChartableAttributes
            }

            // const isIntransitEntityInstanceView
            const selfEntityTemplateId =
                selfEntityAttributeData?.templateId || ''
            const parsedPreSelectedChartableAttributes: string[] = JSON.parse(
                preSelectedChartableAttributes ?? '[]',
            )

            /** Give a thought if the below code block, (till we calcluate selectedValues, selectedRelationList)
                      the  can be rewritten using better logic. */
            const preSelectedSelfAttributes =
                selfEntityAttributeData?.attributes
                    ?.filter((attribute) => {
                        const key = `${attribute.id} (${selfEntityTemplateId})`
                        return parsedPreSelectedChartableAttributes.includes(
                            key,
                        )
                    })
                    .map((item) => {
                        return viewId + item.id
                    }) || []

            const selfRelationsToBePreselected: string[] =
                parsedPreSelectedChartableAttributes
                    .map((attribute) =>
                        attribute.split(' (')[1].replace(')', ''),
                    )
                    .filter(
                        /* We can exclude self Entity Template Id(shipmentEntityTemplateId), 
                            because we don't show it in options for the first dropdown in Graph/Map Widget.*/
                        (entityTemplateId) =>
                            entityTemplateId !== selfEntityTemplateId,
                    )
            const uniqueValuesList = new Set(selfRelationsToBePreselected)
            const relations = entityTemplateRelations
                .filter((relation) =>
                    uniqueValuesList.has(relation.childEntity),
                )
                .map((relation) => {
                    return {
                        relationName: relation.relationName,
                        relatedEntityTemplateId: relation.childEntity,
                    }
                })

            const preSelectedRelationsInstanceIds: string[] = []
            const selectedRelationList: SelectedRelationList[] = []
            relations?.forEach((relation) => {
                relatedEntitiesResponse?.relations?.[
                    relation?.relationName
                ]?.forEach((relatedEntityInstance) => {
                    const relatedEntityInstanceId =
                        relatedEntityInstance.entityInstanceId
                    const selectedAttributes: IAttribute[] = []
                    relatedEntityInstance.attributes.forEach((attribute) => {
                        const key = `${attribute.id} (${relation.relatedEntityTemplateId})`
                        if (
                            parsedPreSelectedChartableAttributes.includes(
                                key,
                            ) &&
                            isChartableAndNotHidden(attribute)
                        ) {
                            selectedAttributes.push(attribute)
                        }
                    })
                    listOfStartDates.push(
                        relatedEntityInstance.relationCreationTime,
                    )
                    listOfEndDates.push(relatedEntityInstance.relationEndTime)
                    const selection: SelectedRelationList = {
                        relatedEntityInstanceId,
                        selectedAttributes,
                    }
                    preSelectedRelationsInstanceIds.push(
                        relatedEntityInstanceId,
                    )
                    selectedRelationList.push(selection)
                })
            })

            if (isIntransitShipmentPreview) {
                /**
                 * In CCL Shipment detailed view, we select the date range as the entire relation duration or shipment duration.
                 */
                const isAtleastOneRelationHasNullEndDate =
                    listOfEndDates.includes(null)
                const isAtleastOneRelationHasNullStartDate =
                    listOfStartDates.includes(null)
                const validEndDates: string[] = listOfEndDates.filter(
                    (date): date is string => date !== null,
                )
                const validStartDates: string[] = listOfStartDates.filter(
                    (date): date is string => date !== null,
                )
                const maxOfRelationEndDates = getMaximumDate(validEndDates)
                const minOfRelationStartDates = getMinimumDate(validStartDates)
                if (!isAtleastOneRelationHasNullEndDate) {
                    initialCustomEndDate = dayjs
                        .utc(maxOfRelationEndDates)
                        .format()
                }
                if (!isAtleastOneRelationHasNullStartDate) {
                    const relationStart = dayjs.utc(minOfRelationStartDates)
                    const earliest = dayjs.utc(
                        earliestDataTime || entityInstanceCreatedDate,
                    )
                    initialCustomStartDate = (
                        earliest.isBefore(relationStart)
                            ? earliest
                            : relationStart
                    ).format()
                } else {
                    initialCustomStartDate = dayjs
                        .utc(earliestDataTime || entityInstanceCreatedDate)
                        .format()
                }
            }
            const selectedValues = [
                ...preSelectedSelfAttributes,
                ...preSelectedRelationsInstanceIds,
            ]
            if (selectedValues.length > 0) {
                return {
                    initialSelectedValues: selectedValues,
                    initialSelectedRelationList: selectedRelationList,
                    initialCustomStartDate,
                    initialCustomEndDate,
                }
            } else {
                return {
                    initialSelectedValues:
                        dataList.length > 0 ? [dataList[0].id] : [],
                    initialSelectedRelationList: dataList[0]?.isRelation
                        ? [
                              initializeSelectedRelationListForRelatedEntity(
                                  dataList[0],
                              ),
                          ]
                        : [],
                    initialCustomStartDate,
                    initialCustomEndDate,
                }
            }
        } else {
            return {
                initialSelectedValues:
                    dataList.length > 0 ? [dataList[0].id] : [],
                initialSelectedRelationList: dataList[0]?.isRelation
                    ? [
                          initializeSelectedRelationListForRelatedEntity(
                              dataList[0],
                          ),
                      ]
                    : [],
                initialCustomStartDate,
                initialCustomEndDate,
            }
        }
    }, [
        /**
         * This useMemo hook is used to initialize selected values, relation list, start date, end date based on preselected attributes from application settings
         * We ensure all it's dependecies are fetched before this component even loads. So that we don't have to re compute this useMemo again. Ideally we should not
         * be adding any dependecies here. But to over come eslint error we are adding them.
         */
        dataList,
        entityTemplateRelations,
        entityInstanceCreatedDate,
        selfEntityAttributeData,
        isIntransitShipmentPreview,
        relatedEntitiesResponse,
        initializeSelectedRelationListForRelatedEntity,
        inTransitSettings?.preSelectedChartableAttributes,
        facilityMonitoringSettings?.preSelectedChartableAttributes,
        isFMAssetPreview,
        viewId,
        earliestDataTime,
    ])
    /**
     * selectedValue represents the selected items(self attributes and relations) in the first dropdown in the Graph/Map widget.
     */
    const [selectedValue, setSelectedValue] = useState<string[]>(
        initialSelectedValues,
    )

    /**
     * selectedRelationList represents the selected attributes of relations in the dropdowns related to relations
     */
    const [selectedRelationList, setSelectedRelationList] = useState<
        SelectedRelationList[]
    >(initialSelectedRelationList)
    const [selectedPeriod, setSelectedPeriod] = useState('Custom')
    const [customStartDate, setCustomStartDate] = useState(
        initialCustomStartDate,
    )
    const [customEndDate, setCustomEndDate] = useState(initialCustomEndDate)
    const [showWithinGeofence, setShowWithinGeofence] = useState(true)

    const displayAttributeSelectionAndDateRange = ![
        // widgetView.MARINE_REPORT_VIEW,
        widgetView.INSURANCE_VIEW,
    ].includes(viewTab)

    const relationsDropdownToDisplay = dataList.filter(
        (i) => selectedValue.includes(i.id) && i.isRelation,
    )

    const currentDateInUTC = useMemo(() => {
        return dayjs.utc().format()
    }, [])

    const { wayPointsSegments, wayPointsSegmentsNames, updatedPeriodOptions } =
        useMemo(() => {
            if (
                !isIntransitShipmentPreview ||
                !selfEntityAttributeData ||
                !inTransitSettings
            ) {
                return {
                    wayPointsSegments: [],
                    wayPointsSegmentsNames: [],
                    updatedPeriodOptions: periodOptions,
                }
            }

            const getUTCDate = (
                dateObj:
                    | { datetime?: string | null; timezone?: string | null }
                    | null
                    | undefined,
            ) => {
                if (!dateObj?.datetime) return ''
                return convertDateToTargetTimezone(
                    dateObj.datetime,
                    dateObj.timezone || 'UTC',
                    'UTC',
                )
            }

            const shipmentStatusValue: string = getAttributeById(
                selfEntityAttributeData?.attributes,
                inTransitSettings?.shipmentStatusAttributeId,
            )?.value
            const shipmentStatusRange = JSON.parse(
                getAttributeById(
                    selfEntityAttributeData?.attributes,
                    inTransitSettings?.shipmentStatusAttributeId,
                )?.range?.trim() || '[]',
            )
            const atOriginValue: string =
                shipmentStatusRange.find(
                    (status: IKeyValuePair) => status.key === 'atOrigin',
                )?.value || ''
            const wayPointsAttributeId = inTransitSettings?.wayPointsAttributeId
            const wayPoints: IShipmentWaypoint[] =
                getAttributeById(
                    selfEntityAttributeData?.attributes,
                    wayPointsAttributeId,
                )?.value ?? []
            const sortedWayPoints = [...wayPoints].sort((a, b) => {
                const dateA = a.actualArrivalDate?.datetime || ''
                const dateB = b.actualArrivalDate?.datetime || ''
                if (!dateA || !dateB) {
                    return 0
                }
                return isDate1BiggerThanDate2(dateA, dateB) ? 1 : -1
            })
            const wayPointsSegments: IWayPointAndPeriod[] = []

            const shipperLocationName =
                formatEntityDisplayLabel(
                    getAttributeById(
                        selfEntityAttributeData?.attributes,
                        inTransitSettings?.originAttributeId,
                    )?.value,
                ) || 'Shipper'

            const receiverLocationName =
                formatEntityDisplayLabel(
                    getAttributeById(
                        selfEntityAttributeData?.attributes,
                        inTransitSettings?.destinationAttributeId,
                    )?.value,
                ) || 'Receiver'

            const destinationCO2Value: number =
                getAttributeById(
                    selfEntityAttributeData?.attributes,
                    'destinationCO2eWTW',
                )?.value ?? null
            const _destinationDistanceValue: number =
                getAttributeById(
                    selfEntityAttributeData?.attributes,
                    'destinationDistance',
                )?.value ?? null
            const destinationDistanceValue: Distance | null =
                _destinationDistanceValue
                    ? {
                          value: _destinationDistanceValue,
                          units:
                              getAttributeById(
                                  selfEntityAttributeData?.attributes,
                                  'destinationDistance',
                              )?.unit || '',
                      }
                    : null
            const actualDepartureDate: IDateTime = getAttributeById(
                selfEntityAttributeData?.attributes,
                inTransitSettings?.actualDepartureDateAttributeId ??
                    'actualDepartureDate',
            )?.value
            const actualDepartureDateInUTC = getUTCDate(actualDepartureDate)

            const actualArrivalDate: IDateTime = getAttributeById(
                selfEntityAttributeData?.attributes,
                inTransitSettings?.actualArrivalDateAttributeId ??
                    'actualArrivalDate',
            )?.value
            const actualArrivalDateInUTC = getUTCDate(actualArrivalDate)

            const firstWayPoint = sortedWayPoints[0]
            const lastWayPoint = sortedWayPoints[sortedWayPoints.length - 1]

            let startTime =
                earliestDataTime || entityInstanceCreatedDate
                    ? dayjs
                          .utc(earliestDataTime || entityInstanceCreatedDate)
                          .format()
                    : ''
            let endTime = actualDepartureDateInUTC
                ? actualDepartureDateInUTC
                : startTime
                  ? currentDateInUTC //Means shipment is present in this segment
                  : ''
            const isShipmentStarted =
                shipmentStatusValue === atOriginValue ||
                !!actualDepartureDateInUTC
            if (isShipmentStarted && shipperLocationName !== 'Shipper') {
                wayPointsSegments.push({
                    segmentName: shipperLocationName,
                    isWithinGeofence: true,
                    startTime,
                    endTime,
                })
            }
            startTime = actualDepartureDateInUTC
            endTime = firstWayPoint
                ? firstWayPoint.actualArrivalDate
                    ? getUTCDate(firstWayPoint.actualArrivalDate)
                    : startTime
                      ? currentDateInUTC
                      : ''
                : actualArrivalDateInUTC //No firstWayPoint means end time equals actual arrival date at destination
                  ? actualArrivalDateInUTC
                  : startTime
                    ? currentDateInUTC
                    : ''
            const segmentName =
                shipperLocationName +
                ' - ' +
                (firstWayPoint
                    ? formatEntityDisplayLabel(firstWayPoint.location)
                    : receiverLocationName)
            if (startTime) {
                wayPointsSegments.push({
                    segmentName,
                    isWithinGeofence: false,
                    startTime,
                    endTime,
                    origin: shipperLocationName,
                    destination: firstWayPoint
                        ? formatEntityDisplayLabel(firstWayPoint?.location)
                        : receiverLocationName,
                    carbonEmission: firstWayPoint
                        ? firstWayPoint.CO2eWTW ?? null
                        : destinationCO2Value,
                    distanceFromPreviousLocation: firstWayPoint
                        ? firstWayPoint.distanceFromPreviousLocation ?? null
                        : destinationDistanceValue,
                    modeOfTransport: firstWayPoint
                        ? firstWayPoint.modeOfTransport
                        : 'land', //Always land from Shipper to first waypoint
                })
            }

            for (let i = 0; i < sortedWayPoints.length - 1; i++) {
                const currentWayPoint = sortedWayPoints[i]
                const nextWayPoint = sortedWayPoints[i + 1]
                let segmentName: string =
                    formatEntityDisplayLabel(currentWayPoint.location) +
                    (currentWayPoint.locode
                        ? ' (' + currentWayPoint.locode + ')'
                        : '')
                let startTime: string = currentWayPoint.actualArrivalDate
                    ?.datetime
                    ? getUTCDate(currentWayPoint.actualArrivalDate)
                    : ''
                let endTime: string = currentWayPoint.actualDepartureDate
                    ?.datetime
                    ? getUTCDate(currentWayPoint.actualDepartureDate)
                    : startTime
                      ? currentDateInUTC //Means shipment is present in this segment of waypoint
                      : ''
                if (startTime) {
                    wayPointsSegments.push({
                        segmentName,
                        isWaypoint: true,
                        isWithinGeofence: true,
                        startTime,
                        endTime,
                        type: currentWayPoint.type,
                    })
                }

                startTime = currentWayPoint.actualDepartureDate?.datetime
                    ? getUTCDate(currentWayPoint.actualDepartureDate)
                    : ''
                endTime = nextWayPoint.actualArrivalDate?.datetime
                    ? getUTCDate(nextWayPoint.actualArrivalDate)
                    : startTime //Means shipment started from current waypoint
                      ? actualArrivalDateInUTC
                          ? actualArrivalDateInUTC //Means shipment reached directly Receiver location without visiting next waypoint
                          : currentDateInUTC //Means shipment is present in this segment from current waypoint to next waypoint
                      : ''
                segmentName =
                    formatEntityDisplayLabel(currentWayPoint.location) +
                    ' - ' +
                    (startTime && nextWayPoint.actualArrivalDate?.datetime
                        ? formatEntityDisplayLabel(nextWayPoint.location)
                        : startTime && actualArrivalDateInUTC
                          ? receiverLocationName
                          : 'Present') //If shipment has not reached the next segment yet. We display date till current time.
                if (startTime) {
                    wayPointsSegments.push({
                        segmentName,
                        isWithinGeofence: false,
                        startTime,
                        endTime,
                        origin: formatEntityDisplayLabel(
                            currentWayPoint.location,
                        ),
                        destination: formatEntityDisplayLabel(
                            nextWayPoint.location,
                        ),
                        carbonEmission: nextWayPoint?.CO2eWTW ?? null,
                        distanceFromPreviousLocation:
                            nextWayPoint?.distanceFromPreviousLocation ?? null,
                        modeOfTransport: nextWayPoint?.modeOfTransport ?? null,
                    })
                }
                if (startTime && !nextWayPoint.actualArrivalDate?.datetime) {
                    /**
                     * Shipment has not reached the next segment yet. We can omit computing further segments.
                     */
                    break
                }
            }
            if (sortedWayPoints.length > 0) {
                let startTime = lastWayPoint.actualArrivalDate?.datetime
                    ? getUTCDate(lastWayPoint.actualArrivalDate)
                    : ''
                let endTime = lastWayPoint.actualDepartureDate?.datetime
                    ? getUTCDate(lastWayPoint.actualDepartureDate)
                    : startTime
                      ? currentDateInUTC //Means shipment is present in this segment
                      : ''
                if (startTime) {
                    wayPointsSegments.push({
                        segmentName: formatEntityDisplayLabel(
                            lastWayPoint.location,
                        ),
                        isWaypoint: true,
                        isWithinGeofence: true,
                        startTime,
                        endTime,
                        type: lastWayPoint.type,
                    })
                }
                startTime = lastWayPoint.actualDepartureDate?.datetime
                    ? getUTCDate(lastWayPoint.actualDepartureDate)
                    : ''
                endTime =
                    actualArrivalDateInUTC ||
                    (startTime ? currentDateInUTC : '')
                const segmentName =
                    formatEntityDisplayLabel(lastWayPoint.location) +
                    ' - ' +
                    (startTime && actualArrivalDateInUTC
                        ? receiverLocationName
                        : 'Present')
                if (startTime) {
                    wayPointsSegments.push({
                        segmentName,
                        isWithinGeofence: false,
                        startTime,
                        endTime,
                        origin: formatEntityDisplayLabel(lastWayPoint.location),
                        destination: receiverLocationName,
                        carbonEmission: destinationCO2Value,
                        distanceFromPreviousLocation: destinationDistanceValue,
                        //modeOfTransport is always land from last waypoint to receiver
                        modeOfTransport: 'land', //lastWayPoint?.modeOfTransport ?? null,
                    })
                }
            }
            startTime = actualArrivalDateInUTC ?? ''
            endTime = entityInstanceEndDate
                ? dayjs.utc(entityInstanceEndDate).format()
                : startTime
                  ? currentDateInUTC
                  : ''
            if (startTime && receiverLocationName !== 'Receiver') {
                wayPointsSegments.push({
                    segmentName: receiverLocationName,
                    isWithinGeofence: true,
                    startTime,
                    endTime,
                })
            }
            const wayPointsSegmentsNames = wayPointsSegments.map(
                (seqment) => seqment.segmentName,
            )
            return {
                wayPointsSegments,
                wayPointsSegmentsNames,
                updatedPeriodOptions: [
                    periodOptions[periodOptions?.length - 1], //Gives Custom Option
                    ...wayPointsSegmentsNames,
                ],
            }
        }, [
            selfEntityAttributeData,
            isIntransitShipmentPreview,
            inTransitSettings,
            currentDateInUTC,
            earliestDataTime,
            entityInstanceCreatedDate,
            entityInstanceEndDate,
        ])

    const carbonEmissionsTableDataFromWaypoints = useMemo<IWayPointAndPeriod[]>(
        () =>
            wayPointsSegments.filter(
                (wayPointsSegment) => !wayPointsSegment.isWithinGeofence,
            ),
        [wayPointsSegments],
    )

    const { endDate, startDate } = useMemo(() => {
        const currentDateTime = dayjs.utc()
        let pastDateTime = dayjs.utc().subtract(1, 'day')

        if (selectedPeriod === 'Last 2 hrs') {
            pastDateTime = dayjs.utc().subtract(2, 'hour')
        } else if (selectedPeriod === 'Last 4 hrs') {
            pastDateTime = dayjs.utc().subtract(4, 'hour')
        } else if (selectedPeriod === 'Last 8 hrs') {
            pastDateTime = dayjs.utc().subtract(8, 'hour')
        } else if (selectedPeriod === 'Last Day') {
            pastDateTime = dayjs.utc().subtract(1, 'day')
        } else if (selectedPeriod === 'Last Week') {
            pastDateTime = dayjs.utc().subtract(7, 'day')
        } else if (selectedPeriod === 'Last Month') {
            pastDateTime = dayjs.utc().subtract(1, 'month')
        } else if (selectedPeriod === 'Custom') {
            return {
                startDate: customStartDate,
                endDate: customEndDate,
            }
        } else if (wayPointsSegmentsNames.includes(selectedPeriod)) {
            const selectedSegment = wayPointsSegments.find(
                (segment) => segment.segmentName === selectedPeriod,
            )
            if (selectedSegment?.startTime && selectedSegment?.endTime) {
                return {
                    startDate: selectedSegment.startTime,
                    endDate: selectedSegment.endTime,
                }
            }
        }

        return {
            startDate: pastDateTime.format(),
            endDate: currentDateTime.format(),
        }
    }, [
        selectedPeriod,
        customEndDate,
        customStartDate,
        wayPointsSegments,
        wayPointsSegmentsNames,
    ])
    const onSelectValue = (id: string, value: any) => {
        setSelectedValue(value)
        let updatedSelectedRelationList: SelectedRelationList[] = []
        if (value?.length) {
            const selectedRelationItems = dataList.filter(
                (i) => value.includes(i.id) && i.isRelation,
            )
            updatedSelectedRelationList = selectedRelationItems.map((item) => {
                const selectedRelation = selectedRelationList.find(
                    (list) =>
                        item.relatedEntityInstance?.entityInstanceId ===
                        list.relatedEntityInstanceId,
                )
                if (selectedRelation) {
                    return selectedRelation
                } else {
                    /**
                     * This is a case where a relation is just selected in the 1st dropdown. For this we push an object
                     * representing an empty array of attributes as selectedAttributes.
                     */
                    const firstAttributeForAutoSelect =
                        item?.relatedEntityInstance?.attributes.find(
                            isIntransitShipmentPreview
                                ? isChartableAndNotHidden
                                : isChartableOrMappableOrTimeSeriesAndNotHidden,
                        )
                    const selectedAttributes = firstAttributeForAutoSelect
                        ? [firstAttributeForAutoSelect]
                        : []
                    return {
                        relatedEntityInstanceId: item.id,
                        selectedAttributes,
                    } as SelectedRelationList
                }
            })
            setSelectedRelationList(updatedSelectedRelationList)
        } else {
            setSelectedRelationList([])
        }
    }

    const onSelectRelationsAttribute = (id: string, value: any) => {
        const updatedSelectedRelationList: SelectedRelationList[] =
            selectedRelationList.map((relationList) => {
                if (relationList.relatedEntityInstanceId === id) {
                    const attributes = dataList
                        .find((item) => item.isRelation && item.id === id)
                        ?.relatedEntityInstance?.attributes.filter(
                            (attribute) => value?.includes(attribute.id),
                        )
                    return {
                        ...relationList,
                        selectedAttributes: attributes,
                    } as SelectedRelationList
                } else {
                    return relationList
                }
            })
        setSelectedRelationList(updatedSelectedRelationList)
    }

    const onChangePeriod = (event: any) => {
        const selectedPeriod = event.target.value
        if (selectedPeriod === 'Custom') {
            setCustomStartDate(initialCustomStartDate)
            setCustomEndDate(initialCustomEndDate)
        }
        setSelectedPeriod(event.target.value)
    }

    const onChangeStartDate = (date: string) => {
        setCustomStartDate(date)
        setSelectedPeriod('Custom')
    }

    const onChangeEndDate = (date: string) => {
        setCustomEndDate(date)
        setSelectedPeriod('Custom')
    }

    const {
        memoizedMappableAttributeColors,
        preSelectedMappableAttributesKeys,
    } = useMemo(() => {
        /**
         * We memoize these colors, because we want to preserve the colors assigned to attributes between rerenders
         */
        const attributeColors: { [key: string]: string } = {}
        const preSelectedMappableAttributesKeys: string[] = []

        let parsedPreSelectedMappableAttributes: string[] = []
        if (isIntransitShipmentPreview) {
            parsedPreSelectedMappableAttributes = JSON.parse(
                inTransitSettings.preSelectedMappableAttributes ?? '[]',
            )
        }
        const selfEntityTemplateId: string =
            selfEntityAttributeData?.templateId || ''

        selfEntityAttributeData?.attributes?.forEach((attribute) => {
            if (isMappable(attribute) || isMappableEntityAttribute(attribute)) {
                attributeColors[
                    `${attribute.id}-${selfEntityAttributeData?.uid}`
                ] = getRandomDarkColor()
                const key = `${attribute.id} (${selfEntityTemplateId})`
                if (parsedPreSelectedMappableAttributes.includes(key)) {
                    preSelectedMappableAttributesKeys.push(
                        viewId + attribute.id,
                    )
                }
            }
        })
        allRelatedEntities.forEach((relatedEntity) => {
            relatedEntity.attributes.forEach((attribute) => {
                if (
                    isMappable(attribute) ||
                    isMappableEntityAttribute(attribute)
                ) {
                    attributeColors[`${attribute.id}-${relatedEntity.uid}`] =
                        getRandomDarkColor()
                    const key = `${attribute.id} (${relatedEntity.templateId})`
                    if (parsedPreSelectedMappableAttributes.includes(key)) {
                        preSelectedMappableAttributesKeys.push(
                            relatedEntity.entityInstanceId + attribute.id,
                        )
                    }
                }
            })
        })

        return {
            memoizedMappableAttributeColors: attributeColors,
            preSelectedMappableAttributesKeys,
        }
    }, [
        selfEntityAttributeData,
        allRelatedEntities,
        isIntransitShipmentPreview,
        inTransitSettings?.preSelectedMappableAttributes,
        viewId,
    ])

    /**
     * Function to extract and transform GeoJSON features
     * @param attributes
     * @param uid
     * @param entityInstanceId
     * @param isSelfEntityInstance refers to whether we are calling this function for main entity instance(selfEntityInstance) or
     * for a related entity instance
     * @returns
     */
    const extractAndTransformGeoJSON = useCallback(
        (
            attributes: IAttribute[] | undefined,
            uid: string,
            entityInstanceId: string,
            isSelfEntityInstance: boolean,
        ): GeoJSONDataSource[] => {
            const createGeoJSONDataSource = (
                attribute: IAttribute,
                geoJSON: GeoJSON.FeatureCollection,
            ) => {
                try {
                    const updatedFeatures: GeoJSON.Feature[] = []
                    geoJSON.features.forEach((feature) => {
                        if (feature.geometry.type === 'Polygon') {
                            const polygonCoordinates =
                                feature.geometry.coordinates[0]
                            const [centerX, centerY] =
                                calculatePolygonCenter(polygonCoordinates)

                            const polygonMarkerFeature = {
                                ...feature,
                                geometry: {
                                    type: 'Point',
                                    coordinates: [centerX, centerY],
                                } as GeoJSON.Point,
                            }
                            updatedFeatures.push(polygonMarkerFeature)
                        }
                        updatedFeatures.push(feature)
                    })

                    const transformedGeoJSON: GeoJSON.FeatureCollection = {
                        ...geoJSON,
                        features: updatedFeatures,
                    }

                    return {
                        attributeId: attribute.id,
                        attributeName: isSelfEntityInstance
                            ? attribute.name
                            : `${attribute.name} (${uid})`,
                        entity: uid,
                        entityInstanceId,
                        geojson: transformedGeoJSON,
                        color: memoizedMappableAttributeColors[
                            `${attribute.id}-${uid}`
                        ],
                    } as GeoJSONDataSource
                } catch (e) {
                    console.error('geoJSON', e)
                    return null
                }
            }
            const getGeoJSON = (attribute: IAttribute) => {
                const parsedAttribute: any = attribute.value || '{}'
                const geoJSON = parsedAttribute?.['geoFence'] || {}
                return geoJSON
            }
            const staticGeoJSONDataSources: GeoJSONDataSource[] = []
            attributes?.forEach((attribute) => {
                if (
                    isMappable(attribute) &&
                    !isTimeSeries(attribute) &&
                    isValidGeoJSON(attribute.value)
                ) {
                    const geoJSON: GeoJSON.FeatureCollection =
                        attribute.value || ({} as any)
                    const geoJSONDataSource = createGeoJSONDataSource(
                        attribute,
                        geoJSON,
                    )
                    if (geoJSONDataSource) {
                        staticGeoJSONDataSources.push(geoJSONDataSource)
                    }
                } else if (
                    isMappableEntityAttribute(attribute) &&
                    !isTimeSeries(attribute) &&
                    isValidGeoJSON(getGeoJSON(attribute))
                ) {
                    const geoJSON: GeoJSON.FeatureCollection =
                        getGeoJSON(attribute)
                    const geoJSONDataSource = createGeoJSONDataSource(
                        attribute,
                        geoJSON,
                    )
                    if (geoJSONDataSource) {
                        staticGeoJSONDataSources.push(geoJSONDataSource)
                    }
                }
            })
            return staticGeoJSONDataSources
        },
        [memoizedMappableAttributeColors],
    )

    const staticGeoJSONCollection = useMemo(() => {
        // Extract and transform GeoJSON from related entities
        const relatedEntitiesGeoJSON: GeoJSONDataSource[] =
            allRelatedEntities.flatMap((relatedEntity) =>
                extractAndTransformGeoJSON(
                    relatedEntity.attributes,
                    relatedEntity.uid,
                    relatedEntity.entityInstanceId,
                    false,
                ),
            )
        // Extract and transform GeoJSON from self entity attributes
        const selfEntityGeoJSON: GeoJSONDataSource[] =
            extractAndTransformGeoJSON(
                selfEntityAttributeData?.attributes,
                selfEntityAttributeData?.uid,
                selfEntityAttributeData?.entityId,
                true,
            )
        // Combine the results
        return [...relatedEntitiesGeoJSON, ...selfEntityGeoJSON]
    }, [
        selfEntityAttributeData,
        allRelatedEntities,
        extractAndTransformGeoJSON,
    ])

    const { selectedAttributes } = useMemo(() => {
        const selectedAttributes: ISelectedAttributes[] = [] //Holds all the selected attributes in all dropdowns
        dataList.forEach((item) => {
            if (!item.isRelation) {
                if (selectedValue.includes(viewId + item.selfAttribute?.id)) {
                    const attribute: ISelectedAttributes = {
                        entityInstanceId: viewId,
                        attribute: item.selfAttribute!,
                    }
                    selectedAttributes.push(attribute)
                }
            } else {
                const selectedRelation = selectedRelationList.find(
                    (relation) => relation.relatedEntityInstanceId === item.id,
                )
                selectedRelation?.selectedAttributes?.forEach(
                    (selectedAttribute) => {
                        const attribute: ISelectedAttributes = {
                            entityInstanceId:
                                selectedRelation?.relatedEntityInstanceId,
                            attribute: selectedAttribute,
                        }
                        selectedAttributes.push(attribute)
                    },
                )
            }
        })
        return {
            selectedAttributes,
        }
    }, [dataList, selectedValue, selectedRelationList, viewId])

    const debounceValue = 1000
    const debouncedSelectedAttributes = useDebounce(
        selectedAttributes,
        debounceValue,
    )
    const debouncedStartDate = useDebounce(startDate, debounceValue)
    const debouncedEndDate = useDebounce(endDate, debounceValue)

    const mapDataQueryKey = useMemo(
        () => [
            'mapData',
            viewId,
            debouncedSelectedAttributes,
            debouncedStartDate,
            debouncedEndDate,
            orgId,
            filterOutlierLocations,
            isIntransitShipmentPreview,
            isGuestUser,
            guestUserPreferredUnit,
            user,
        ],
        [
            viewId,
            debouncedSelectedAttributes,
            debouncedStartDate,
            debouncedEndDate,
            orgId,
            filterOutlierLocations,
            isIntransitShipmentPreview,
            isGuestUser,
            guestUserPreferredUnit,
            user,
        ],
    )

    const { data: mapApiResponse, isLoading } = useQuery({
        queryKey: mapDataQueryKey,
        queryFn: async () => {
            if (!debouncedStartDate || !debouncedEndDate) {
                return null
            }

            const attributes = isIntransitShipmentPreview
                ? [
                      ...allMappableTimeSeriesAttributes,
                      ...debouncedSelectedAttributes,
                  ]
                : debouncedSelectedAttributes

            const includeDeviceAttributesForShipment =
                isIntransitShipmentPreview
            const includeHistoricData = isIntransitShipmentPreview

            return await fetchMapData(
                viewId,
                attributes,
                debouncedStartDate,
                debouncedEndDate,
                orgId,
                filterOutlierLocations,
                includeDeviceAttributesForShipment,
                includeHistoricData,
                isGuestUser,
                guestUserPreferredUnit,
            )
        },
        enabled: !!debouncedStartDate && !!debouncedEndDate,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    })

    const { gpsData, timeSeriesMappableAttributesKeys } = useMemo(() => {
        if (!mapApiResponse) {
            return {
                gpsData: [],
                timeSeriesMappableAttributesKeys: [],
            }
        }

        const timeSeriesGeoJSONCollection: GeoJSONDataSource[] = []
        mapApiResponse?.dataSets?.forEach((dataset) => {
            const coordinates: GeoJSON.Position[] = []
            const validMapData = dataset.mapData.filter(isValidMapData)
            // const uniqueLocations = getLatestUniqueLocations(validMapData)
            // console.log('UniqueLocations', uniqueLocations)
            const features: Array<
                GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>
            > = validMapData.map((item, index) => {
                coordinates.push([item.long, item.lat])
                return {
                    type: 'Feature',
                    properties: {
                        ...item,
                        config: {
                            icon: index === 0 ? 'marker' : 'dot',
                            color: '',
                        },
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [item.long, item.lat],
                    },
                }
            })

            /**
             * In future, check if this lineFeature can be omitted here, but somehow we can draw this line connecting
             * all the GPS points(Device Locations) in DynamicDataSourceMap component by using (new google.maps.Polyline).
             * Check if this is better approach or the polyline way is better approach. And proceed.
             */
            const lineFeature: GeoJSON.Feature<
                GeoJSON.Geometry,
                GeoJSON.GeoJsonProperties
            > = {
                type: 'Feature',
                properties: {
                    config: {
                        icon: 'none',
                        color: 'none',
                    },
                },
                geometry: {
                    type: 'LineString',
                    coordinates,
                },
            }
            features.push(lineFeature)

            const geoJSON: GeoJSON.FeatureCollection = {
                type: 'FeatureCollection',
                features,
            }
            const attributeName = dataset.label
            /**
             * For self entity case, dataset.uid is null.
             * For Related entity case, dataset.uid is not null.
             */
            const uid = dataset.uid ? dataset.uid : selfEntityAttributeData?.uid
            const attributeId: string = dataset?.attributeId
            const dataSource: GeoJSONDataSource = {
                attributeId,
                attributeName,
                entity: uid,
                entityInstanceId: dataset.entityInstanceId,
                geojson: geoJSON,
                color: memoizedMappableAttributeColors[`${attributeId}-${uid}`],
            }
            timeSeriesGeoJSONCollection.push(dataSource)
        })

        const timeSeriesMappableAttributesKeys =
            timeSeriesGeoJSONCollection.map((dataSource) =>
                getDataSourceKey(dataSource),
            )
        const allMapData: GeoJSONDataSource[] = [
            ...timeSeriesGeoJSONCollection,
            ...staticGeoJSONCollection,
        ]

        return {
            gpsData: allMapData,
            timeSeriesMappableAttributesKeys,
        }
    }, [
        mapApiResponse,
        selfEntityAttributeData?.uid,
        memoizedMappableAttributeColors,
        staticGeoJSONCollection,
    ])

    const timeSeriesAttributesCCLShipmentPreview = useMemo(() => {
        const currentLocationAttribute =
            getAttributeById(
                selfEntityAttributeData?.attributes,
                inTransitSettings.locationAttributeId,
            ) ?? ({} as IAttribute)
        return [
            ...debouncedSelectedAttributes,
            { entityInstanceId: viewId, attribute: currentLocationAttribute },
        ]
    }, [
        debouncedSelectedAttributes,
        selfEntityAttributeData,
        inTransitSettings.locationAttributeId,
        viewId,
    ])

    const filteredMapData = useMemo(() => {
        return gpsData
            ?.filter((mapData) => {
                /**
                 * Below isSelected represents whether the attribute is selected or not in the dropdowns.
                 * In the CCL Shipment preview we pass all the mappable attributes to MultiDataSourceMapComponent Component.
                 * The legends presents in MultiDataSourceMapComponent are used to display the mappable attributes.
                 * In Normal instance preview both the legends and checkboxes in dropdown are used to display the mappable attributes.
                 */
                if (isIntransitShipmentPreview) {
                    return true
                }
                const isSelected = selectedAttributes.some(
                    (attribute) =>
                        attribute.entityInstanceId + attribute.attribute.id ===
                        mapData.entityInstanceId + mapData.attributeId,
                )
                return isSelected
            })
            .map((dataSource) => {
                /**
                 * By default in this Parent component we set is as true for normal instance preview.
                 * For CCL Shipment preview, this is based on preselected mappable attributes set in CCL Settings.
                 * In child component when user clicks on map legend,
                 * we toggle of the attribute by setting isSelected as false
                 */
                let isSelected = true
                if (isIntransitShipmentPreview) {
                    isSelected = preSelectedMappableAttributesKeys.includes(
                        dataSource.entityInstanceId + dataSource.attributeId,
                    )
                }
                return {
                    ...dataSource,
                    isSelected,
                }
            })
    }, [
        selectedAttributes,
        gpsData,
        isIntransitShipmentPreview,
        preSelectedMappableAttributesKeys,
    ])

    const onExpandGraph = () => {
        setGraphExpand(!graphExpand)
    }

    const isCritical = isCriticalAlarm(alarmStatus)

    const dataSources: GeoJSONDataSource[] = useMemo(() => {
        if (!isIntransitShipmentPreview) {
            return filteredMapData
        } else {
            // return []
            /**
             * In this else case (Intransit specific view), we preconfigure the color codes, icon svgs(or urls).
             * These preconfigured fields are used in MultiDataSourceMapComponent(DynamicDataSourceMap.tsx) component
             * to differentiate the Google Markers, Paths, Legends
             */
            const iconConfig = {
                [`${inTransitSettings.locationAttributeId}`]: {
                    // url: '/assets/images/map/marker-blue.png',
                    path: locationMarkerSVG,
                    color: isCritical ? '#FF0000' : '#006400', //#CC6CE7 is the old value
                    legendSVG: 'LocationMarker',
                },
                [`${inTransitSettings?.originAttributeId}`]: {
                    // url: '/assets/images/map/marker-blue.png',
                    path: locationMarkerSVG,
                    color: '#0077E6',
                    legendSVG: 'LocationMarker',
                },
                [`${inTransitSettings?.destinationAttributeId}`]: {
                    // url: '/assets/images/map/marker-blue.png',
                    path: locationMarkerSVG,
                    color: '#FF00AA',
                    legendSVG: 'LocationMarker',
                },
                [`${inTransitSettings?.AISLocationAttributeId}`]: {
                    url: '/assets/images/map/Vessel_Fill_AIS_Orange.svg',
                    // path: locationMarkerSVG,
                    color: '#ff6600',
                    strokeOpacity: 1,
                    repeat: '0',
                    legendSVG: 'VesselIcon',
                },
                [`${inTransitSettings?.pinLocationAttributeId}`]: {
                    url: '/assets/images/map/Vessel_Fill_Pin_Yellow.svg',
                    // path: locationMarkerSVG,
                    color: '#e6e600',
                    strokeOpacity: 1,
                    repeat: '0',
                    legendSVG: 'VesselIcon',
                },
                [`${inTransitSettings?.locationsGeoJSONAttributeId}`]: {
                    url: '/assets/images/map/MarineLocations.svg',
                    // path: portIconSVG,
                    color: '#00008B', //Color is taken from MarineLocations.svg
                    legendSVG: 'SeaPortIcon',
                },
                [`${inTransitSettings?.pathGeoJSONAttributeId}`]: {
                    // url: '/assets/images/map/marker-green.png',
                    // path: locationMarkerSVG,
                    color: '#595959',
                    strokeOpacity: 0,
                    repeat: '20px',
                    legendSVG: 'DashedLine',
                },
                [`${inTransitSettings?.deviceLocationAttributeId}`]: {
                    // url: '/assets/images/map/marker-green.png',
                    path: locationMarkerSVG,
                    color: isCritical ? '#FF0000' : '#006400',
                    strokeOpacity: 1,
                    repeat: '0',
                    legendSVG: 'LocationMarker',
                },
            }

            const updatedGpsData = filteredMapData.map((data) => {
                const key = `${data.attributeId}` // You can use data.attributeName directly
                const updatedFeatures = data.geojson.features.map(
                    (feature: any) => {
                        const properties = feature?.properties || {}
                        const config = {
                            ...properties?.config,
                            ...(iconConfig[key] || {}),
                        }
                        return {
                            ...feature,
                            properties: { ...properties, config },
                        }
                    },
                )
                return {
                    ...data,
                    geojson: {
                        ...data.geojson,
                        features: updatedFeatures,
                    },
                } as GeoJSONDataSource
            })
            return updatedGpsData
        }
    }, [
        filteredMapData,
        isIntransitShipmentPreview,
        inTransitSettings?.locationAttributeId,
        inTransitSettings?.originAttributeId,
        inTransitSettings?.destinationAttributeId,
        inTransitSettings?.AISLocationAttributeId,
        inTransitSettings?.pinLocationAttributeId,
        inTransitSettings?.locationsGeoJSONAttributeId,
        inTransitSettings?.pathGeoJSONAttributeId,
        inTransitSettings?.deviceLocationAttributeId,
        isCritical,
    ])

    useEffect(() => {
        // Only reset local state if the data in Redux has updated to match the current viewId
        // This ensures a smooth transition and correct defaults for the new entity
        if (entityData?.selfEntityAttributeData?.entityId === viewId) {
            /**
             * This is fix for UBQ-4418
             */
            setSelectedValue(initialSelectedValues)
            setSelectedRelationList(initialSelectedRelationList)
            setCustomStartDate(initialCustomStartDate)
            setCustomEndDate(initialCustomEndDate)
        }
    }, [
        viewId,
        entityData?.selfEntityAttributeData?.entityId,
        initialSelectedValues,
        initialSelectedRelationList,
        initialCustomStartDate,
        initialCustomEndDate,
    ])

    return (
        <>
            <Box>
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        background: '#DCECF9',
                        padding: '8px 10px',
                        borderRadius: '5px 5px 0px 0px',
                    }}
                >
                    <ViewEntityTabs
                        isIntransitShipmentPreview={isIntransitShipmentPreview}
                        shouldDisplayGeneralReportsTab={
                            shouldDisplayGeneralReportsTab
                        }
                        shouldDisplayMapViewTab={!isIntransitShipmentPreview}
                        widgetView={widgetView}
                        relatedEntitiesResponse={relatedEntitiesResponse}
                        entityTemplateRelations={entityTemplateRelations || []}
                        enabledApplications={enabledApplication2}
                    />
                    <Icon
                        sx={{
                            marginLeft: '20px',
                            color: '#00000081',
                            cursor: 'pointer',
                        }}
                        onClick={() => onExpandGraph()}
                        id="expand-graph"
                    >
                        {graphExpand ? <FullscreenExit /> : <Fullscreen />}
                    </Icon>
                </Box>
                <Box
                    sx={{
                        background: '#F5FBFF',
                    }}
                >
                    {displayAttributeSelectionAndDateRange && (
                        <ViewEntityAttributeFilterPanel
                            selectedValue={selectedValue}
                            onSelectValue={onSelectValue}
                            dataList={dataList}
                            relationsDropdownToDisplay={
                                relationsDropdownToDisplay
                            }
                            selectedRelationList={selectedRelationList}
                            onSelectRelationsAttribute={
                                onSelectRelationsAttribute
                            }
                            selectedPeriod={selectedPeriod}
                            onChangePeriod={onChangePeriod}
                            periodOptions={updatedPeriodOptions}
                            startDate={startDate}
                            endDate={endDate}
                            setStartDate={onChangeStartDate}
                            setEndDate={onChangeEndDate}
                            entityInstanceCreatedDate={
                                earliestDataTime || entityInstanceCreatedDate
                            }
                            entityInstanceEndDate={entityInstanceEndDate}
                            showWithinGeofence={showWithinGeofence}
                            setShowWithinGeofence={setShowWithinGeofence}
                            isIntransitShipmentPreview={
                                isIntransitShipmentPreview
                            }
                        />
                    )}
                    <Box sx={{ width: '100%' }}>
                        {viewTab === widgetView.GRAPH_APACHE_ECHART_VIEW && (
                            <>
                                <ApacheEcharts
                                    viewId={viewId}
                                    selectedAttributes={
                                        debouncedSelectedAttributes
                                    }
                                    startDate={debouncedStartDate}
                                    endDate={debouncedEndDate}
                                    orgId={orgId}
                                    wayPointsSegments={wayPointsSegments}
                                    showWithinGeofence={showWithinGeofence}
                                    clampToRelationStart={isFMAssetPreview}
                                />
                                {/* TODO: => ADD ENUM BASED ON APPLICATION VIEW  */}
                                {isIntransitShipmentPreview ? (
                                    dataSources?.length > 0 ? (
                                        <Box
                                            sx={{
                                                height: '600px',
                                                width: '100%',
                                                opacity: isLoading ? 0.5 : 1,
                                            }}
                                        >
                                            <MultiDataSourceMapComponent
                                                alarmCritical={isCritical}
                                                dataSources={dataSources}
                                                dynamicDataLayersKeys={
                                                    timeSeriesMappableAttributesKeys
                                                }
                                                preConfiguredIcons={
                                                    isIntransitShipmentPreview
                                                }
                                                shipmentStatus={shipmentStatus}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography
                                            sx={{
                                                fontStyle: 'italic',
                                                textAlign: 'center',
                                                color: 'rgba(0, 0, 0, 0.6)',
                                                paddingBottom: '20px',
                                            }}
                                        >
                                            {t('noMapToDisplay')}
                                        </Typography>
                                    )
                                ) : (
                                    <> </>
                                )}
                            </>
                        )}
                        {!isIntransitShipmentPreview &&
                            viewTab === widgetView.MAP_VIEW &&
                            (dataSources?.length > 0 ? (
                                <Box
                                    sx={{
                                        height: '600px',
                                        width: '100%',
                                        opacity: isLoading ? 0.5 : 1,
                                    }}
                                >
                                    <MultiDataSourceMapComponent
                                        alarmCritical={isCritical}
                                        dataSources={dataSources}
                                        dynamicDataLayersKeys={
                                            timeSeriesMappableAttributesKeys
                                        }
                                        preConfiguredIcons={
                                            isIntransitShipmentPreview
                                        }
                                        shipmentStatus={shipmentStatus}
                                    />
                                </Box>
                            ) : (
                                <Typography
                                    sx={{
                                        fontStyle: 'italic',
                                        textAlign: 'center',
                                        color: 'rgba(0, 0, 0, 0.6)',
                                        paddingBottom: '20px',
                                    }}
                                >
                                    {t('noMapToDisplay')}
                                </Typography>
                            ))}
                        {viewTab === widgetView.MARINE_REPORT_VIEW && (
                            <MaritimeReport
                                inTransitSettings={inTransitSettings}
                                searatesEntityInstances={
                                    searatesEntityInstances
                                }
                            />
                        )}
                        {viewTab === widgetView.CARBON_REPORT_VIEW && (
                            <CarbonReportFromWaypoints
                                carbonEmissionsTableDataFromWaypoints={
                                    carbonEmissionsTableDataFromWaypoints
                                }
                            />
                        )}
                        {viewTab === widgetView.INSURANCE_VIEW && <Insurance />}
                        {viewTab === widgetView.GENERAL_REPORT_VIEW && (
                            <GeneralReportView
                                reportSettings={reportSettings}
                                selfEntityAttributeData={
                                    selfEntityAttributeData
                                }
                                orgId={orgId}
                                startDate={startDate}
                                endDate={endDate}
                                entityInstanceCreatedDate={dayjs(
                                    earliestDataTime ||
                                        entityInstanceCreatedDate,
                                ).format()}
                                disableGenerate={false}
                                selectedAttributes={debouncedSelectedAttributes}
                                includeStage={isIntransitShipmentPreview}
                            />
                        )}
                        {/* {isWorkflowView && <Workflow />} */}
                        {viewTab === widgetView.ALERTS_VIEW &&
                            (isIntransitShipmentPreview ? (
                                <Alerts
                                    showStatus={true}
                                    orgId={orgId}
                                    entityId={viewId}
                                />
                            ) : (
                                <Alerts orgId={orgId} entityId={viewId} />
                            ))}
                        {viewTab === widgetView.ANALYSIS_VIEW && (
                            <>
                                <AnalysisView
                                    viewId={viewId}
                                    selectedAttributes={
                                        debouncedSelectedAttributes
                                    }
                                    startDate={debouncedStartDate}
                                    endDate={debouncedEndDate}
                                    orgId={orgId}
                                    wayPointsSegments={wayPointsSegments}
                                    selectedPeriod={selectedPeriod}
                                />
                                <TimeSeriesDataTable
                                    selectedAttributes={
                                        timeSeriesAttributesCCLShipmentPreview
                                    }
                                    orgId={orgId}
                                    endDate={debouncedEndDate}
                                    startDate={debouncedStartDate}
                                    viewID={viewId}
                                    filterOutlierLocations={
                                        filterOutlierLocations
                                    }
                                    wayPointsSegments={wayPointsSegments}
                                    selectedPeriod={selectedPeriod}
                                    includeStage={isIntransitShipmentPreview}
                                />
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
            {/* TO DO: move this time series data table under graph view and map view components (or where ever needed) */}
            {!isIntransitShipmentPreview &&
                (viewTab === widgetView.GRAPH_APACHE_ECHART_VIEW ||
                    viewTab === widgetView.MAP_VIEW) && (
                    <TimeSeriesDataTable
                        selectedAttributes={debouncedSelectedAttributes}
                        orgId={orgId}
                        endDate={debouncedEndDate}
                        startDate={debouncedStartDate}
                        viewID={viewId}
                        filterOutlierLocations={filterOutlierLocations}
                        wayPointsSegments={wayPointsSegments}
                        selectedPeriod={selectedPeriod}
                        includeStage={isIntransitShipmentPreview}
                        clampToRelationStart={isFMAssetPreview}
                    />
                )}
        </>
    )
}

const TabsViewComponent = memo(TabsView)

export default TabsViewComponent
